// Settings → Products: which product field groups show (the business's kinds are in
// Settings → Business), the
// business's own custom fields, and its brands.
import type { JsonValue, RowOf, TenantSettingsRow } from '@ims/data'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Field,
  IconButton,
  Input,
  Select,
  TD,
  TH,
  Table,
  useToast,
  SkeletonForm,
  SkeletonTable,
  Switch,
} from '@ims/ui'
import { Plus, Trash2 } from '@ims/ui/icons'
import { useState } from 'react'
import { useLookups } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import {
  FIELD_GROUPS,
  businessKinds,
  customFields,
  defaultGroups,
  enabledGroups,
  fieldKey,
  type AttributeField,
  type AttributeType,
  type GroupId,
} from '../lib/catalog.ts'
import { kindsLabel } from './kind-picker.tsx'
import { SaveBar } from './settings-part.tsx'
import { useUnsavedWarning } from '../lib/unsaved.ts'
import { deleteOp, insertOp, newBrand, updateOp } from '../lib/rows.ts'
import { parsePrefixes, readScaleConfig, scaleConfigJson } from '../lib/scale.ts'
import {
  DEFAULT_SCALE_CONFIG,
  parseScaleBarcode,
  scaleConfigProblem,
  type ScaleConfig,
} from '@ims/core'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'

const TYPE_LABELS: Record<AttributeType, string> = {
  text: 'Text',
  number: 'Number',
  date: 'Date',
  yesno: 'Yes / No',
  select: 'List of choices',
}

function useCommit() {
  const repo = useRepo()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const commit = async (ops: Parameters<typeof repo.commit>[0], message: string) => {
    setBusy(true)
    try {
      await repo.commit(ops)
      toast.success(message)
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }
  return { commit, busy }
}

export function CatalogSettings() {
  const settings = useSettings()
  if (!settings) return <SkeletonForm />
  return (
    <div className="space-y-4">
      <ProductFieldsForm key={settings.updated_at} settings={settings} />
      <BrandSettings />
    </div>
  )
}

/** The scale barcode boxes as typed, from the stored layout. */
function scaleFormOf(settings: TenantSettingsRow) {
  const stored = readScaleConfig(settings.scale_barcode) ?? DEFAULT_SCALE_CONFIG
  return {
    prefixes: stored.prefixes.join(', '),
    itemDigits: String(stored.itemDigits),
    valueDigits: String(stored.valueDigits),
    value: stored.value,
    decimals: String(stored.decimals),
  }
}

function ProductFieldsForm({ settings }: { settings: TenantSettingsRow }) {
  const canEdit = useCan('admin')
  const { commit, busy } = useCommit()
  const toast = useToast()
  const kinds = businessKinds(settings)
  const [groups, setGroups] = useState<Set<GroupId>>(() => enabledGroups(settings))
  const [fields, setFields] = useState<AttributeField[]>(() => customFields(settings))
  const [newLabel, setNewLabel] = useState('')
  const [newType, setNewType] = useState<AttributeType>('text')
  const [alertDays, setAlertDays] = useState(String(settings.expiry_alert_days))
  const [expiredSale, setExpiredSale] = useState(settings.expired_sale)
  const [minPriceRule, setMinPriceRule] = useState(settings.min_price_rule)
  const [allowNegative, setAllowNegative] = useState(settings.allow_negative_stock)
  const [customItems, setCustomItems] = useState(settings.custom_items)
  const [restaurant, setRestaurant] = useState(settings.restaurant)
  const [scale, setScale] = useState(() => scaleFormOf(settings))
  const scaleConfig: ScaleConfig = {
    prefixes: parsePrefixes(scale.prefixes),
    itemDigits: Number(scale.itemDigits),
    valueDigits: Number(scale.valueDigits),
    value: scale.value,
    decimals: Number(scale.decimals),
  }
  const scaleProblem = groups.has('scale') ? scaleConfigProblem(scaleConfig) : null

  // Changes wait for Save: the floating bar keeps it in view (SaveBar, as Business does).
  const draft = {
    groups: [...groups].sort(),
    fields,
    alertDays,
    expiredSale,
    minPriceRule,
    allowNegative,
    customItems,
    restaurant,
    scale: groups.has('scale') ? scale : null,
  }
  const saved = {
    groups: [...enabledGroups(settings)].sort(),
    fields: customFields(settings),
    alertDays: String(settings.expiry_alert_days),
    expiredSale: settings.expired_sale,
    minPriceRule: settings.min_price_rule,
    allowNegative: settings.allow_negative_stock,
    customItems: settings.custom_items,
    restaurant: settings.restaurant,
    scale: groups.has('scale') ? scaleFormOf(settings) : null,
  }
  const dirty = canEdit && JSON.stringify(draft) !== JSON.stringify(saved)
  useUnsavedWarning(dirty)
  const discard = () => {
    setGroups(enabledGroups(settings))
    setFields(customFields(settings))
    setAlertDays(String(settings.expiry_alert_days))
    setExpiredSale(settings.expired_sale)
    setMinPriceRule(settings.min_price_rule)
    setAllowNegative(settings.allow_negative_stock)
    setCustomItems(settings.custom_items)
    setRestaurant(settings.restaurant)
    setScale(scaleFormOf(settings))
    setNewLabel('')
  }

  const toggle = (id: GroupId, on: boolean) =>
    setGroups((g) => {
      const next = new Set(g)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const addField = () => {
    const label = newLabel.trim()
    if (!label) return
    setFields((f) => [
      ...f,
      {
        key: fieldKey(label, new Set(f.map((x) => x.key))),
        label,
        type: newType,
        options: newType === 'select' ? [] : undefined,
      },
    ])
    setNewLabel('')
    setGroups((g) => new Set(g).add('custom'))
  }

  const save = () => {
    if (scaleProblem) return toast.error(`Scale barcodes: ${scaleProblem}`)
    const custom: JsonValue = fields.map((f) => ({
      key: f.key,
      label: f.label,
      type: f.type,
      ...(f.type === 'select' ? { options: f.options ?? [] } : {}),
    }))
    void commit(
      [
        updateOp('tenant_settings', settings, {
          field_groups: FIELD_GROUPS.filter((g) => groups.has(g.id)).map((g) => g.id),
          custom_fields: custom,
          expiry_alert_days: Math.min(365, Math.max(0, Math.round(Number(alertDays) || 0))),
          expired_sale: expiredSale,
          min_price_rule: minPriceRule,
          allow_negative_stock: allowNegative,
          custom_items: customItems,
          restaurant,
          scale_barcode: groups.has('scale')
            ? scaleConfigJson(scaleConfig)
            : settings.scale_barcode,
        }),
      ],
      'Product fields saved',
    )
  }

  return (
    <Card>
      <h2 className="font-semibold">Product fields</h2>
      <p className="mb-4 text-sm text-muted">
        Choose what your products need. Hidden fields keep their values.
      </p>
      <p className="text-sm">
        <span className="text-muted">You sell as </span>
        <span className="font-semibold">{kindsLabel(kinds)}</span>
        <span className="text-muted"> (change it in Settings → Business).</span>
        {canEdit && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-1"
            onClick={() => setGroups(new Set(defaultGroups(kinds)))}
          >
            Tick their fields
          </Button>
        )}
      </p>
      <Switch
        className="mt-4 max-w-sm"
        label="Restaurant mode"
        description="Tables and open orders: a bill stays open while people eat. Set up tables under Settings → Tables."
        checked={restaurant}
        disabled={!canEdit}
        onChange={(e) => setRestaurant(e.target.checked)}
      />

      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {FIELD_GROUPS.map((g) => (
          <li key={g.id} className="rounded-xl border border-border p-3">
            <Checkbox
              label={
                <span className="font-medium">
                  {g.label}
                  {!g.available && (
                    <Badge tone="neutral" className="ml-2">
                      Coming soon
                    </Badge>
                  )}
                </span>
              }
              checked={g.available && groups.has(g.id)}
              disabled={!canEdit || !g.available}
              onChange={(e) => toggle(g.id, e.target.checked)}
            />
            <p className="mt-1 pl-6 text-xs text-muted">{g.description}</p>
          </li>
        ))}
      </ul>

      {groups.has('batches') && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field
            label="Warn about expiry (days before)"
            hint="Batches expiring within this many days show as “expiring soon”."
          >
            {(id) => (
              <Input
                id={id}
                inputMode="numeric"
                value={alertDays}
                disabled={!canEdit}
                onChange={(e) => setAlertDays(e.target.value.replace(/\D/g, ''))}
              />
            )}
          </Field>
          <Field label="Selling expired stock">
            {(id) => (
              <Select
                id={id}
                value={expiredSale}
                disabled={!canEdit}
                onChange={(e) => setExpiredSale(e.target.value as typeof expiredSale)}
              >
                <option value="warn">Allow, with a warning</option>
                <option value="block">Don’t allow</option>
              </Select>
            )}
          </Field>
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field
          label="Selling more than is in stock"
          hint="Checked at the counter, even offline. Managers and owners can always go ahead."
        >
          {(id) => (
            <Select
              id={id}
              value={allowNegative ? 'warn' : 'block'}
              disabled={!canEdit}
              onChange={(e) => setAllowNegative(e.target.value === 'warn')}
            >
              <option value="warn">Allow, after a warning</option>
              <option value="block">Don’t allow staff</option>
            </Select>
          )}
        </Field>
        <Field
          label="Custom items on bills"
          hint="A line typed by hand (a repair charge, a one-off item). It does not touch stock."
        >
          {(id) => (
            <Select
              id={id}
              value={customItems}
              disabled={!canEdit}
              onChange={(e) => setCustomItems(e.target.value as typeof customItems)}
            >
              <option value="everyone">Everyone who bills</option>
              <option value="managers">Managers only</option>
              <option value="off">Off</option>
            </Select>
          )}
        </Field>
      </div>

      {groups.has('wholesale') && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field
            label="Selling below the minimum price"
            hint="Managers and owners can always sell below it."
          >
            {(id) => (
              <Select
                id={id}
                value={minPriceRule}
                disabled={!canEdit}
                onChange={(e) => setMinPriceRule(e.target.value as typeof minPriceRule)}
              >
                <option value="warn">Allow, with a warning</option>
                <option value="block">Don’t allow staff</option>
              </Select>
            )}
          </Field>
        </div>
      )}

      {groups.has('scale') && (
        <ScaleSettings
          value={scale}
          onChange={(change) => setScale((s) => ({ ...s, ...change }))}
          config={scaleConfig}
          problem={scaleProblem}
          disabled={!canEdit}
        />
      )}

      <div className="mt-6">
        <h3 className="text-sm font-semibold">Custom fields</h3>
        <p className="mb-2 text-xs text-muted">
          Extra details you want on every product, like “Rack no.” or “Country of origin”.
        </p>
        {fields.length > 0 && (
          <ul className="mb-3 divide-y divide-border rounded-xl border border-border">
            {fields.map((f, i) => (
              <li key={f.key} className="flex flex-wrap items-center gap-3 px-3 py-2">
                <Input
                  aria-label="Field name"
                  value={f.label}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setFields((all) =>
                      all.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                    )
                  }
                  className="w-48"
                />
                <span className="text-xs text-muted">{TYPE_LABELS[f.type]}</span>
                {f.type === 'select' && (
                  <Input
                    aria-label={`Choices for ${f.label}`}
                    placeholder="Choices, separated by commas"
                    value={(f.options ?? []).join(', ')}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setFields((all) =>
                        all.map((x, j) =>
                          j === i
                            ? {
                                ...x,
                                options: e.target.value
                                  .split(',')
                                  .map((o) => o.trim())
                                  .filter(Boolean),
                              }
                            : x,
                        ),
                      )
                    }
                    className="min-w-56 flex-1"
                  />
                )}
                {canEdit && (
                  <IconButton
                    label={`Remove ${f.label}`}
                    onClick={() => setFields((all) => all.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <div className="flex flex-wrap items-end gap-3">
            <Field label="New field" className="w-56">
              {(id) => (
                <Input
                  id={id}
                  value={newLabel}
                  placeholder="Rack no."
                  onChange={(e) => setNewLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addField())}
                />
              )}
            </Field>
            <Field label="Type" className="w-44">
              {(id) => (
                <Select
                  id={id}
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as AttributeType)}
                >
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Button
              variant="secondary"
              icon={<Plus className="size-4" />}
              disabled={!newLabel.trim()}
              onClick={addField}
            >
              Add field
            </Button>
          </div>
        )}
      </div>

      {canEdit ? (
        <SaveBar
          inCard
          dirty={dirty}
          busy={busy}
          saveLabel="Save product fields"
          onSave={save}
          onDiscard={discard}
        />
      ) : (
        <p className="mt-4 text-xs text-muted">Only admins can change product fields.</p>
      )}
    </Card>
  )
}

function useBrandUsage() {
  return (
    useLive(
      async (repo) => {
        const counts = new Map<string, number>()
        for (const p of (await repo.list('products', { limit: 1_000_000 })).rows) {
          if (p.brand_id) counts.set(p.brand_id, (counts.get(p.brand_id) ?? 0) + 1)
        }
        return counts
      },
      [],
      ['products'],
    ).data ?? new Map<string, number>()
  )
}

function BrandSettings() {
  const lookups = useLookups()
  const usage = useBrandUsage()
  const canEdit = useCan('staff')
  const canDelete = useCan('manager')
  const { current } = useWorkspace()
  const { commit, busy } = useCommit()
  const [name, setName] = useState('')
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null)
  const [removing, setRemoving] = useState<RowOf<'brands'> | null>(null)
  const brands = lookups.brands
  const clash = (value: string, self?: string) =>
    brands.some((b) => b.id !== self && b.name.trim().toLowerCase() === value.trim().toLowerCase())

  return (
    <Card className="p-0">
      <div className="p-4 pb-2">
        <h2 className="font-semibold">Brands</h2>
      </div>
      {!lookups.loaded && <SkeletonTable rows={3} cols={3} />}
      {brands.length > 0 && (
        <Table>
          <thead>
            <tr>
              <TH>Brand</TH>
              <TH align="right">Products</TH>
              <TH />
            </tr>
          </thead>
          <tbody>
            {brands.map((b) => (
              <tr key={b.id}>
                <TD className="font-medium">
                  {editing?.id === b.id ? (
                    <Input
                      aria-label="Brand name"
                      value={editing.name}
                      onChange={(e) => setEditing({ id: b.id, name: e.target.value })}
                      className="max-w-xs"
                    />
                  ) : (
                    b.name
                  )}
                </TD>
                <TD align="right">{usage.get(b.id) ?? 0}</TD>
                <TD align="right" className="whitespace-nowrap">
                  {editing?.id === b.id ? (
                    <>
                      <Button
                        size="sm"
                        loading={busy}
                        disabled={!editing.name.trim() || clash(editing.name, b.id)}
                        onClick={async () => {
                          const ok = await commit(
                            [updateOp('brands', b, { name: editing.name.trim() })],
                            'Brand renamed',
                          )
                          if (ok) setEditing(null)
                        }}
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing({ id: b.id, name: b.name })}
                        >
                          Rename
                        </Button>
                      )}
                      {canDelete && !usage.get(b.id) && (
                        <Button size="sm" variant="ghost" onClick={() => setRemoving(b)}>
                          Delete
                        </Button>
                      )}
                    </>
                  )}
                </TD>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-end gap-3 border-t border-border p-4">
          <Field label="New brand" className="w-64" error={clash(name) ? 'Already added' : null}>
            {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Button
            icon={<Plus className="size-4" />}
            loading={busy}
            disabled={!name.trim() || clash(name) || !current}
            onClick={async () => {
              if (!current) return
              const ok = await commit(
                [
                  insertOp(
                    'brands',
                    newBrand({ tenantId: current.tenant_id }, { name: name.trim() }),
                  ),
                ],
                `Added ${name.trim()}`,
              )
              if (ok) setName('')
            }}
          >
            Add brand
          </Button>
        </div>
      )}
      <ConfirmDialog
        open={removing !== null}
        title="Delete brand"
        message={removing ? `Delete “${removing.name}”?` : undefined}
        confirmLabel="Delete"
        danger
        busy={busy}
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (removing && (await commit([deleteOp('brands', removing.id)], 'Brand deleted'))) {
            setRemoving(null)
          }
        }}
      />
    </Card>
  )
}

interface ScaleDraft {
  prefixes: string
  itemDigits: string
  valueDigits: string
  value: 'weight' | 'price'
  decimals: string
}

/** The label scale's barcode layout, with a box to test a real sticker. */
function ScaleSettings({
  value,
  onChange,
  config,
  problem,
  disabled,
}: {
  value: ScaleDraft
  onChange: (change: Partial<ScaleDraft>) => void
  config: ScaleConfig
  problem: string | null
  disabled: boolean
}) {
  const [test, setTest] = useState('')
  const reading = problem || !test.trim() ? null : parseScaleBarcode(test, config)
  const product = useLive(
    async (r) =>
      reading
        ? ((await r.list('products', { where: { plu_code: reading.plu }, limit: 1 })).rows[0] ??
          null)
        : null,
    [reading?.plu ?? null],
    ['products'],
  ).data
  const digits = (v: string) => v.replace(/\D/g, '').slice(0, 2)
  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold">Scale barcodes</h3>
      <p className="mb-2 text-xs text-muted">
        How your label scale builds its 13-digit barcode: prefix, item code (PLU), then the weight
        or price, then a check digit. Example: 21 00123 00450 9 = item 123, 0.450 kg.
      </p>
      <div className="grid gap-3 sm:grid-cols-5">
        <Field label="Prefixes" hint="Comma separated.">
          {(id) => (
            <Input
              id={id}
              value={value.prefixes}
              disabled={disabled}
              onChange={(e) => onChange({ prefixes: e.target.value })}
            />
          )}
        </Field>
        <Field label="Item code digits">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              value={value.itemDigits}
              disabled={disabled}
              onChange={(e) => onChange({ itemDigits: digits(e.target.value) })}
            />
          )}
        </Field>
        <Field label="Value digits">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              value={value.valueDigits}
              disabled={disabled}
              onChange={(e) => onChange({ valueDigits: digits(e.target.value) })}
            />
          )}
        </Field>
        <Field label="Value is">
          {(id) => (
            <Select
              id={id}
              value={value.value}
              disabled={disabled}
              onChange={(e) =>
                onChange({
                  value: e.target.value === 'price' ? 'price' : 'weight',
                  decimals: e.target.value === 'price' ? '2' : '3',
                })
              }
            >
              <option value="weight">Weight</option>
              <option value="price">Price</option>
            </Select>
          )}
        </Field>
        <Field label="Decimals">
          {(id) => (
            <Input
              id={id}
              inputMode="numeric"
              value={value.decimals}
              disabled={disabled}
              onChange={(e) => onChange({ decimals: digits(e.target.value) })}
            />
          )}
        </Field>
      </div>
      {problem && <p className="mt-2 text-sm text-danger">{problem}</p>}
      <Field label="Test a sticker" className="mt-3 sm:max-w-sm">
        {(id) => (
          <Input
            id={id}
            value={test}
            placeholder="Scan a scale sticker"
            onChange={(e) => setTest(e.target.value)}
          />
        )}
      </Field>
      {test.trim() && !problem && (
        <p className="mt-2 text-sm" role="status">
          {!reading
            ? 'Not a scale barcode with this layout (or the check digit is wrong).'
            : `Item ${reading.plu}${product ? ` (${product.name})` : ' (no product has this code)'} · ${
                reading.qtyMilli !== undefined
                  ? `weight ${(reading.qtyMilli / 1000).toFixed(3)}`
                  : `price ${(reading.amountPaisa / 100).toFixed(2)}`
              }`}
        </p>
      )}
    </div>
  )
}
